/**
 * PS1 mood — shared settings for the low-fidelity sacred look.
 *
 * Deliberately NOT a framebuffer post-processing stack. A real post chain would
 * mean every renderer drawing into an FBO and a composite pass on top, which is
 * a large restructure for effects that can be had far more cheaply:
 *
 *  - **Vertex snapping** is a uniform each shader applies itself. This is the
 *    single most recognisable part of the look — the console had no sub-pixel
 *    precision, so geometry twitched between whole pixels as the camera moved.
 *  - **Low internal resolution** is a reduced backing-store scale plus
 *    `image-rendering: pixelated` in CSS. It gives the chunky upscale AND buys
 *    back a large amount of mobile frame budget, which matters now that icons,
 *    text and the sun are all point clouds competing with a SLAM loop.
 *  - **Dither and colour quantisation** are a static tiled overlay in CSS
 *    rather than a shader pass. Against the gold gradients that is where most of
 *    the character comes from, and it costs one composited layer.
 *
 * Fog lives per-renderer, because the depth it needs is renderer-specific.
 */

export interface Ps1Settings {
  enabled: boolean;
  /**
   * Vertex snap grid in DEVICE pixels. Too fine and it is invisible; too coarse
   * and point clouds read as noise rather than style. 2 is legible on a phone.
   */
  snapPx: number;
  /**
   * Backing-store scale. 0.5 halves the rendered pixels in each axis. Below
   * ~0.4 point clouds lose their structure entirely.
   */
  resolutionScale: number;
  /** Strength of the dither/scanline overlay, 0..1. */
  dither: number;
}

export const PS1: Ps1Settings = {
  enabled: true,
  snapPx: 2,
  resolutionScale: 0.6,
  dither: 0.5,
};

/**
 * Effective device-pixel ratio for a point-cloud canvas.
 *
 * Capped at 2 regardless: at 3x on a phone this is 9x the fragments for no
 * visible gain on soft dots, and the frame budget is shared with tracking.
 */
export function ps1Dpr(deviceDpr: number): number {
  const capped = Math.min(deviceDpr, 2);
  return PS1.enabled ? capped * PS1.resolutionScale : capped;
}

/** Apply or remove the nearest-neighbour upscale on a canvas element. */
export function applyPs1Upscale(canvas: HTMLCanvasElement): void {
  canvas.style.imageRendering = PS1.enabled ? 'pixelated' : '';
}
